import React from 'react';

import classNames from 'classnames';

import ArrowDownIcon from '@/assets/icons/arrow-down.svg?react';
import useInputChangeValue from '@/hooks/common/useInputChangeValue';
import usePrevious from '@/hooks/common/usePrevious';

import styles from './Select.module.scss';

type Value = string | null;

export interface OptionObject {
  name: string;
  value: NonNullable<Value>;
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  innerRef?: React.Ref<HTMLInputElement>;
}

interface Props {
  placeholder?: string;
  options: OptionObject[];
  onChange?: (val: Value) => void;
  onBlur?: () => void;
  value?: Value;
  inputProps?: InputProps;
  [key: `data-${string}`]: string;
}

const Select: React.FC<Props> = ({
  placeholder,
  options,
  onChange,
  onBlur,
  value = null,
  inputProps: { innerRef, ...inputProps } = { innerRef: undefined },
  ...dataAttributes
}) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>();

  const { changeValue } = useInputChangeValue();

  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const prevIsOpen = usePrevious<boolean>(isOpen);

  const [selectedValue, _setSelectedValue] = React.useState<Value>(value);
  const setSelectedValue = React.useCallback(
    (val: Value) => {
      if (val !== selectedValue) {
        _setSelectedValue(val);

        if (inputRef.current) {
          changeValue(inputRef.current, val);
        }

        onChange?.(val);
      }
    },
    [onChange, selectedValue, changeValue],
  );

  const toggle = React.useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleDocumentClick = React.useCallback((ev: Event) => {
    const wrapper = wrapperRef.current;
    const isInWrapper =
      wrapper?.contains(ev.target as Node) && wrapper !== ev.target;
    if (isInWrapper) return;

    setIsOpen(false);
  }, []);

  const controlDocumentHandleClick = React.useCallback(
    (attach: boolean) => {
      ['click', 'touchstart'].forEach((type) =>
        document[attach ? 'addEventListener' : 'removeEventListener'](
          type,
          handleDocumentClick,
          true,
        ),
      );
    },
    [handleDocumentClick],
  );

  React.useEffect(() => {
    if (isOpen) {
      controlDocumentHandleClick(true);
    } else {
      controlDocumentHandleClick(false);
    }

    return () => {
      controlDocumentHandleClick(false);
    };
  }, [isOpen, controlDocumentHandleClick]);

  // 閉じたらinputからblur (blurをトリガーとするRHF等への対応)
  React.useEffect(() => {
    if (prevIsOpen && !isOpen) {
      inputRef.current?.focus();
      inputRef.current?.blur();

      onBlur?.();

      // style上の整合性のため
      wrapperRef.current?.focus();
      wrapperRef.current?.blur();
    }
  }, [isOpen, prevIsOpen, onBlur]);

  // optionsが変化した場合にselectedValueが適正でなければ空にする
  React.useEffect(() => {
    if (selectedValue) {
      if (!options.map((opt) => opt.value).includes(selectedValue)) {
        setSelectedValue(null);
      }
    }
    // optionsはarrayのため同値性比較でfalseになるのでstringifyで同一性比較
  }, [JSON.stringify(options), selectedValue]);

  // componentのvalue変化でもselectedValueをset
  React.useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  // inputのvalueに値がある場合(componentの切り替えやback時)にSelectの表示側にも値をsetする
  React.useEffect(() => {
    if (inputRef.current?.value) setSelectedValue(inputRef.current.value);
  }, [inputRef.current?.value, setSelectedValue]);

  const handleOnClickOptions = React.useCallback(
    (ev: React.MouseEvent) => {
      const target = (ev.target as HTMLElement).closest('[data-value]');
      if (target) {
        const value = target.getAttribute('data-value') || null;
        setSelectedValue(value);
      }

      setIsOpen(false);
    },
    [setSelectedValue],
  );

  return (
    <div
      ref={wrapperRef}
      tabIndex={0}
      role="listbox"
      className={styles.wrapper}
      {...dataAttributes}
    >
      <input
        defaultValue={selectedValue || value || ''}
        {...inputProps}
        readOnly
        type="text"
        className={styles.hiddenInput}
        ref={(node) => {
          inputRef.current = node || undefined;
          if (typeof innerRef === 'function') {
            innerRef(node);
          } else if (innerRef) {
            (
              innerRef as React.MutableRefObject<HTMLInputElement | null>
            ).current = node;
          }
        }}
      />
      <div
        onClick={toggle}
        className={classNames(styles.handle, {
          [styles.isPlaceholder]: !selectedValue,
          [styles.isOpen]: isOpen,
        })}
        aria-label="select-handle"
      >
        <div aria-label="select-selected-option">
          {options.find(({ value }) => value === selectedValue)?.name ||
            placeholder}
        </div>
        <ArrowDownIcon className={styles.arrow} />
      </div>
      <div
        className={classNames(styles.options, {
          [styles.isOpen]: isOpen,
        })}
        onClick={handleOnClickOptions}
        aria-label="select-options"
        hidden={!isOpen}
      >
        {selectedValue && placeholder && (
          <div
            data-value=""
            className={classNames(styles.option, styles.isPlaceholder)}
          >
            {placeholder}
          </div>
        )}
        {options.map(({ name, value }) => (
          <div
            key={value}
            data-value={value}
            className={classNames(styles.option, {
              [styles.selected]: value === selectedValue,
            })}
          >
            <div>{name}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Select;
